import React, { useCallback, useRef, useState } from 'react';
import cn from 'classnames';

import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import Space from 'components/generic/Space.component';
import CurrencySign from 'components/price/CurrencySign.component';
import CHARGE_TYPE from 'constants/payment/chargeType.const';
import S from 'constants/string.const';
import useTip from 'hooks/useTip.hook';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';

import CN from './ChargeFeeForm.module.scss';
// NOTE: This component may no longer be used, as it is part of the old modal flow
const ChargeFeeForm = ({ totalPrice, isOpen, onRequestClose, onConfirmCharge }) => {
  const { val: isFixedPrice, yay: setIsFixed, nay: removeFixedPrice } = useTip(true);
  const { val: isButtonDisabled, yay: enableButton } = useTip(false);
  const [fixedFee, setFixedFee] = useState('');
  const [percentFee, setPercentFee] = useState('');

  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const fixedFeeRef = useRef('');
  const percentFeeRef = useRef('');

  const onSelectFixed = useCallback(() => {
    setIsFixed();
    setPercentFee('');
    setMessage('');
    setError('');
  }, [setIsFixed]);

  const onSelectPercent = useCallback(() => {
    removeFixedPrice();
    setFixedFee('');

    setMessage('');
    setError('');
  }, [removeFixedPrice]);

  const handleFixedChange = (value) => {
    setFixedFee(value);
    fixedFeeRef.current = value;
    if (parseFloat(value) > totalPrice) {
      setError(
        <div>
          The fixed fee cannot exceed the total price of <CurrencySign value={totalPrice} />
        </div>,
      );
    } else {
      setError('');
    }
    let parsedValue = parseFloat(value);
    if (!isNaN(parsedValue)) {
      setMessage(
        <div>
          Slick will charge your client{S.apos}s card <CurrencySign />
          {parsedValue}
          which is <CurrencySign value={(parsedValue / totalPrice) * 100} />% of the booking price
        </div>,
      );
    }
  };

  const handleFixedBlur = () => {
    let parsedValue = parseFloat(fixedFee);
    if (!isNaN(parsedValue)) {
      parsedValue = Math.min(parsedValue, totalPrice);
      setFixedFee(parsedValue.toFixed(2));
      fixedFeeRef.current = parsedValue.toFixed(2);
    }
  };

  const handlePercentChange = (value) => {
    setPercentFee(value);
    percentFeeRef.current = value;
    if (parseFloat(value) > 100) {
      setError(`The percentage cannot exceed 100%`);
    } else {
      setError('');
    }
    let parsedValue = parseFloat(value);
    if (!isNaN(parsedValue)) {
      const fee = (totalPrice * parsedValue) / 100;
      setMessage(
        <div>
          Slick will charge your client{S.apos}s card {parsedValue}% of the booking price which is
          <Space>
            <CurrencySign value={fee} />
          </Space>
        </div>,
      );
    }
  };

  const handlePercentBlur = () => {
    let parsedValue = parseFloat(percentFee);
    if (!isNaN(parsedValue)) {
      parsedValue = Math.min(parsedValue, 100);
      setPercentFee(parsedValue.toFixed(2));
      percentFeeRef.current = parsedValue.toFixed(2);
    }
  };

  const handleCharge = useCallback(() => {
    enableButton();
    let price = isFixedPrice
      ? parseFloat(fixedFeeRef.current || 0)
      : (totalPrice * parseFloat(percentFeeRef.current || 0)) / 100;

    onConfirmCharge({}, price, CHARGE_TYPE.noShowCharge);
  }, [enableButton, isFixedPrice, onConfirmCharge, totalPrice]);

  const isButtonDisabledFinal =
    (isFixedPrice && !fixedFee) || (!isFixedPrice && !percentFee) || error || isButtonDisabled;

  return (
    <SalonModal tag="ChargeFeeForm" isOpen={fromAnyToBool(isOpen)} withFooter>
      <SalonModal.Header>
        <div className={CN.closeButtonContainer}>
          <Button variant="empty" onClick={() => onRequestClose()}>
            <Icon variant="close" variantInvert />
          </Button>
        </div>
      </SalonModal.Header>
      <SalonModal.Body>
        <div className={CN.modalContent}>
          <IconStatus variant="info" width="lg" position="left">
            <Icon variant="pay" selected width="lg" />
          </IconStatus>
          <div className={CN.modalTitle}>This booking has Slick no-show protection enabled</div>
          <div className={CN.modalText}>
            Enter an amount of your choice and Slick will charge the client{S.apos}s card on your behalf.
          </div>
          <div className={CN.modalSubTitle}>
            Total booking price:
            <div className={CN.highlighted}>
              <CurrencySign value={totalPrice} />
            </div>
          </div>

          <div className={CN.fixedRow}>
            <div className={CN.option} onClick={onSelectFixed} role="button" tabIndex={0}>
              <Icon variant="radioCheck" selected={isFixedPrice} width="lg" />
              <span className={CN.label}>
                Fixed (<CurrencySign />)
              </span>
            </div>

            <input
              className={CN.input}
              type="number"
              value={fixedFee}
              onChange={(e) => handleFixedChange(e.target.value)}
              onBlur={handleFixedBlur}
              placeholder="Enter amount"
              step="0.1"
              max={totalPrice}
              disabled={!isFixedPrice}
            />
          </div>
          <div className={CN.percentRow}>
            <div className={CN.option} onClick={onSelectPercent} role="button" tabIndex={0}>
              <Icon variant="radioCheck" selected={!isFixedPrice} width="lg" />
              <span className={CN.label}>Percentage (%)</span>
            </div>

            <input
              className={CN.input}
              type="number"
              value={percentFee}
              onChange={(e) => handlePercentChange(e.target.value)}
              onBlur={handlePercentBlur}
              placeholder="%"
              step="0.10"
              max="100"
              disabled={isFixedPrice}
            />
          </div>
          {message && <div className={CN.message}>{message}</div>}
          {error && <div className={CN.error}>{error}</div>}
        </div>
      </SalonModal.Body>
      <SalonModal.Footer>
        <div
          className={cn({
            'form-footer': true,
            [CN.fullWidth]: true,
          })}
        >
          <Button variant="outlineForce" onClick={() => onRequestClose('no charge')}>
            <div className={CN.buttonGap}>DON{S.apos}T CHARGE</div>
          </Button>
          <Button variant="primaryForce" onClick={handleCharge} disabled={isButtonDisabledFinal}>
            <div className={CN.buttonGap}>CHARGE CLIENT</div>
          </Button>
        </div>
      </SalonModal.Footer>
    </SalonModal>
  );
};

export default ChargeFeeForm;
